import type { CollisionStrategy, Placement, Side } from "./types";

interface DefaultPositioning {
  placement: Side;
  offset: number;
  boundaryMargin: number;
  collisionStrategy: CollisionStrategy;
}

/** Wider positioning input — accepts Placement (e.g. "top-start") from parent props */
interface PositioningInput {
  placement?: Placement;
  offset?: number;
  boundaryMargin?: number;
  collisionStrategy?: CollisionStrategy;
}

/**
 * Default positioning values for dropdown content components (Select, Combobox).
 * Single source of truth — used in prop defaults and positioning computed.
 */
export const DEFAULT_POSITIONING: DefaultPositioning = {
  placement: "bottom",
  offset: 4,
  boundaryMargin: 8,
  collisionStrategy: "flip",
};

/**
 * Resolves positioning by merging defaults → local (from content props) → parent (from root component props).
 * Used by Select/Combobox content composables.
 */
export const resolvePositioning = (
  parent: PositioningInput | undefined,
  local: { placement?: Placement; offset?: number },
) => {
  return {
    ...DEFAULT_POSITIONING,
    ...stripUndefined(local),
    ...stripUndefined(parent),
  };
};

/** Removes keys with undefined values so spread won't override defaults. */
const stripUndefined = <T extends object>(obj: T | undefined): Partial<T> => {
  if (!obj) return {};
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
};
